import { Turbo } from '@hotwired/turbo-rails'

import { Application, defaultSchema } from '@hotwired/stimulus'
import { registerControllers } from 'stimulus-vite-helpers'
import './stylesheets/application.scss'
import ToastController from '../../components/toast_component/toast_controller'

const customSchema = {
  ...defaultSchema,
  keyMappings: {
    ...defaultSchema.keyMappings,
    del: 'Backspace',
  },
}

const application = window.staticApplication || Application.start(document.documentElement, customSchema)
window.staticApplication = application

const coreControllers = import.meta.glob(
  [
    '../controllers/application_controller.js',
    '../controllers/static/new_controller.js', // Add the problematic controller here,
    '../controllers/static/accordion_controller.js',
  ],
  { eager: true }
)

application.register('toast', ToastController)
registerControllers(application, coreControllers)

// --- Static Controllers (Always Load) ---
// Import and register all static controllers (including subfolders)
const staticControllers = import.meta.glob(
  '../controllers/static/**/*_controller.js',
  { eager: true }
)
registerControllers(application, staticControllers)

const featureControllers = import.meta.glob([
  '../controllers/*_controller.js',
  '../controllers/**/*_controller.js',
])

// Track registered controllers to avoid duplicate registration
const registeredControllers = new Set()

Object.keys(coreControllers).forEach(path => {
  const nameMatch = path.match(/\/controllers\/(?:(.+)\/)?(.+)_controller\.js$/)
  if (nameMatch) {
    const namespace = nameMatch[1]
    const controllerName = nameMatch[2]
    const fullName = namespace
      ? `${namespace.replace(/\//g, '--')}--${controllerName}`
      : controllerName
    registeredControllers.add(fullName)
  }
})

Object.keys(staticControllers).forEach(path => {
  const nameMatch = path.match(/\/controllers\/(.+)\/(.+)_controller\.js$/)
  if (nameMatch) {
    const namespace = nameMatch[1]
    const controllerName = nameMatch[2]
    const fullName = `${namespace.replace(/\//g, '--')}--${controllerName}`
    registeredControllers.add(fullName)
  }
})

// Function to scan the page and load necessary controllers
function loadRequiredControllers() {
  console.debug('Scanning for controllers to load...')
  const activeControllers = new Set()

  document.querySelectorAll('[data-controller]').forEach(element => {
    element.getAttribute('data-controller').split(' ').forEach(name => {
      activeControllers.add(name)
    })
  })

  console.debug('Found controllers on page:', [...activeControllers])

  // Try to load controllers that are on the page and not yet registered
  activeControllers.forEach(name => {
    if (registeredControllers.has(name)) {
      return console.debug(`Controller already registered: ${name}`)
    }

    // Determine controller path
    let controllerPath

    // Handle namespaced controllers (with --)
    if (name.includes('--')) {
      const parts = name.split('--')
      const namespace = parts.slice(0, -1).join('/')
      const controller = parts[parts.length - 1].replace(/-/g, '_') // Convert hyphens to underscores
      controllerPath = `../controllers/${namespace}/${controller}_controller.js`
    } else {
      // Handle hyphenated names by converting to underscore
      const underscoreName = name.replace(/-/g, '_')
      controllerPath = `../controllers/${underscoreName}_controller.js`
    }

    console.debug(`Attempting to load controller ${name} from path ${controllerPath}`)

    // Try to load the controller if it exists in our lazy-loaded modules
    if (featureControllers[controllerPath]) {
      featureControllers[controllerPath]().then(module => {
        if (module && module.default) {
          application.register(name, module.default)
          registeredControllers.add(name)
          console.debug(`Successfully lazy loaded controller: ${name}`)
        } else {
          console.error(`Controller module invalid for ${name}`)
        }
      }).catch(err => {
        console.error(`Failed to load controller ${name}:`, err)
      })
    } else {
      console.warn(`Controller path not found for ${name}: ${controllerPath}`)

      // Try alternative path if the controller has dashes in the name
      if (name.includes('-') && !name.includes('--')) {
        const dashedPath = `../controllers/${name}_controller.js`
        if (featureControllers[dashedPath]) {
          console.debug(`Trying alternative path: ${dashedPath}`)
          featureControllers[dashedPath]().then(module => {
            if (module && module.default) {
              application.register(name, module.default)
              registeredControllers.add(name)
              console.debug(`Successfully lazy loaded controller with alternative path: ${name}`)
            }
          }).catch(err => {
            console.error(`Failed to load controller with alternative path ${name}:`, err)
          })
        }
      }
    }
  })
}

// Set up comprehensive event listeners for Turbo navigation
document.addEventListener('DOMContentLoaded', () => {
  loadRequiredControllers()
})

// Listen for all relevant Turbo events
document.addEventListener('turbo:load', () => {
  loadRequiredControllers()
})

document.addEventListener('turbo:render', () => {
  loadRequiredControllers()
})

document.addEventListener('turbo:frame-render', () => {
  loadRequiredControllers()
})

document.addEventListener('turbo:before-frame-render', () => {
  loadRequiredControllers()
})

document.addEventListener('ajax:complete', () => {
  loadRequiredControllers()
})

const observer = new MutationObserver((mutations) => {
  let shouldLoad = false

  mutations.forEach(mutation => {
    if (mutation.type === 'attributes' &&
      mutation.attributeName === 'data-controller') {
      shouldLoad = true
    }

    if (mutation.type === 'childList' && mutation.addedNodes.length) {
      mutation.addedNodes.forEach(node => {
        if (node.nodeType === Node.ELEMENT_NODE) {
          if (node.hasAttribute('data-controller') ||
            node.querySelector('[data-controller]')) {
            shouldLoad = true
          }
        }
      })
    }
  })

  if (shouldLoad) {
    loadRequiredControllers()
  }
})

// Start observing the document body for controller changes
observer.observe(document.body, {
  childList: true,
  subtree: true,
  attributes: true,
  attributeFilter: ['data-controller']
})
